import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { FormInput } from '../../../../components/forms/FormInput';
import { Button } from '../../../../components/buttons/Button';
import { getProduct } from '../../../../data/products/getProduct';
import { Discount } from '../../../../types/Discounts/Discount';
import { requestDomainDiscount } from '../../../../data/discounts/requestDomainDiscount';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { getUserDomainDiscount } from '../../../../data/discounts/getUserDomainDiscount';
import { markDiscountUsed } from '../../../../data/discounts/markCouponUsed';
import { getPaymentIntent } from '../../../../data/payment/getPaymentIntent';
import { StripeCheckout } from '../../../../components/payment/StripeCheckout';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { NavForward } from '../../../../components/buttons/NavForward';
import { updatePaymentInfoDocPayload } from '../../../../types/Payment/updatePaymentInfoDocPayload';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import { FormSelect } from '../../../../components/forms/FormSelect';

export const Route = createFileRoute('/_authenticated/$patientId/payment/selfPay')({
  component: SelfPay,
});

function SelfPay() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const { mutate: mutateChartDoc } = useUpdateChartSection();
  const [alreadyPaid, setAlreadyPaid] = React.useState(false);
  const [globalDiscounts, setGlobalDiscounts] = React.useState<Discount[]>([]);
  const [productCost, setProductCost] = React.useState(349);
  const [discountAmt, setDiscountAmt] = React.useState(0);
  const [discountApplied, setDiscountApplied] = React.useState(false);
  const [clientSecret, setClientSecret] = React.useState('');
  const [readyToPay, setReadyToPay] = React.useState(false);
  const [originalProductCost, setOriginalProductCost] = React.useState(999);
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [checkingDiscount, setCheckingDiscount] = React.useState(false);
  const [requestingDiscount, setRequestingDiscount] = React.useState(false);
  const [hasDiscount, setHasDiscount] = React.useState(false);
  const [partnerOrg, setPartnerOrg] = React.useState('select');
  const [referralCode, setReferralCode] = React.useState('select');
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;

  const codeForm = useForm({
    defaultValues: {
      email: '',
      discountCode: '',
    },
    onSubmit: async ({ value }) => {
      await handleDomainDiscountRequest(value.email);
    },
  });

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'selfPay', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo?.navObj) {
      if (chart.PAYMENT_INFO?.alreadyPaid) {
        setNextPageURL(`../../../${patientId}/payment/thanks`);
      } else {
        (async () => {
          const awaitedUrl = await getNextPage({
            currentPage: 'selfPay',
            chart: chart,
            patientInfo: patientInfo,
          });
          setNextPageURL(awaitedUrl);
        })();
      }
    }
  }, [chart, patientInfo, patientId]);

  const handleDomainDiscountRequest = async (email: string) => {
    //TODO: DECIDE WHETHER TO APPLY THE DISCOUNT AUTOMATICALLY OR FORCE DISCOUNT CODE ENTRY. CURRENTLY, WE FORCE ENTRY
    setHasDiscount(true);
    setRequestingDiscount(true);
    // SET EMAIL AS CONTACT EMAIL
    mutatePatientDoc(
      {
        patientId: patientId,
        contactEmail: email,
      },
      {
        onSuccess: async () => {
          // REQUEST DOMAIN-SPECIFIC DISCOUNT
          const domain = email.split('@')[1];
          const res = await requestDomainDiscount(domain, patientId, chart.PRODUCT_INFO.selectedProduct);
          setRequestingDiscount(false);
          if (res.discountCode) {
            alert(
              'We received your request for a discount. Please check your email for the discount code. If you do not receive an email within 24 hours, please contact support.',
            );
          } else {
            alert(`Error sending discount request email. ${res.message}`);
          }
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const updatePaymentInfoDoc = (payload: updatePaymentInfoDocPayload) => {
    mutateChartDoc(
      {
        patientId: patientId,
        docData: {
          PAYMENT_INFO: {
            alreadyPaid: payload.alreadyPaid,
            amountPaid: payload.amountPaid,
            couponCode: payload.couponCode,
            discountApplied: payload.discountApplied,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleApplyDiscount = async () => {
    // PULL THE CODE FROM THE FORM
    const code = codeForm.state.values.discountCode;

    // CHECK IF DOMAIN DISCOUNT EXISTS FOR THIS USER
    const existingDiscount = await getUserDomainDiscount(patientId);

    // IF SO, AND THEY MATCH; APPLY THE DISCOUNT
    if (existingDiscount?.discountCode === code && !existingDiscount.used) {
      setDiscountAmt(parseInt(existingDiscount.discount));
      setProductCost(originalProductCost - parseInt(existingDiscount.discount));
      setDiscountApplied(true);
      await markDiscountUsed({
        code: existingDiscount.discountCode,
        discount: existingDiscount.discount,
        domain: existingDiscount.domain,
        product: existingDiscount.product,
        uid: patientId,
      });
      updatePaymentInfoDoc({
        alreadyPaid: originalProductCost - parseInt(existingDiscount.discount) == 0 ? true : false,
        amountPaid: originalProductCost - parseInt(existingDiscount.discount),
        couponCode: codeForm.state.values.discountCode,
        discountApplied: existingDiscount.discount,
      });
    } else {
      // CHECK IF THE CODE IS A GLOBAL DISCOUNT CODE
      setCheckingDiscount(true);
      const globalDiscount = globalDiscounts.find((item) => item.name === code);
      if (globalDiscount) {
        // IF SO, APPLY THE DISCOUNT
        setDiscountAmt(parseInt(globalDiscount.discount));
        setProductCost(originalProductCost - parseInt(globalDiscount.discount));
        setDiscountApplied(true);
        await markDiscountUsed({
          code: globalDiscount.name,
          discount: globalDiscount.discount,
          domain: 'global',
          product: chart.PRODUCT_INFO.selectedProduct,
          uid: patientId,
        });
        updatePaymentInfoDoc({
          alreadyPaid: productCost - parseInt(globalDiscount.discount) === 0 ? true : false,
          amountPaid: originalProductCost - parseInt(globalDiscount.discount),
          couponCode: codeForm.state.values.discountCode,
          discountApplied: parseInt(globalDiscount.discount),
        });
        setCheckingDiscount(false);
      } else {
        alert(
          'Invalid discount code. If you believe this is an error, please refresh the page and try again. If you continue to have issues, please contact support.',
        );
        setCheckingDiscount(false);
      }
    }
  };

  const showPayNowButton = () => {
    // PATIENT ALREADY PAID
    if (productCost == 0) {
      return false;
    }
    // PATIENT IS PAYING IN FULL
    if (partnerOrg == 'no' && referralCode == 'no') {
      return true;
    }
    // PATIENT SUCCESSFULLY APPLIED A PARTIAL DISCOUNT
    if (hasDiscount && discountAmt != 0) {
      return true;
    }
    return false;
  };

  // SET THE STRIPE CLIENT SECRET
  React.useEffect(() => {
    if (readyToPay && patientInfo?.contactEmail) {
      const getPaymentIntentAsync = async () => {
        const paymentIntent = await getPaymentIntent(
          productCost * 100, // CONVERT TO CENTS FOR STRIPE
          patientInfo?.contactEmail ? patientInfo.contactEmail : 'info@mideoid.com',
        );
        if (paymentIntent?.client_secret) {
          setClientSecret(paymentIntent.client_secret);
        } else {
          console.error('Error getting payment intent');
        }
      };
      getPaymentIntentAsync();
    }
  }, [readyToPay, productCost, patientInfo?.contactEmail]);

  // SET THE GLOBAL DISCOUNTS
  React.useEffect(() => {
    if (chart?.PAYMENT_INFO?.alreadyPaid) {
      setAlreadyPaid(chart.PAYMENT_INFO.alreadyPaid);
    }
    const init = async () => {
      let productKey = 'vip';
      switch (chart?.PRODUCT_INFO?.selectedProduct) {
        case 'essential':
          productKey = 'essential';
          break;
        case 'vital':
          productKey = 'vital';
          break;
        case 'vip':
          productKey = 'vip';
          break;
        case 'Self Guided Package':
          productKey = 'essential';
          break;
        case 'Vital Package':
          productKey = 'vital';
          break;
        case 'Physician Guided Package':
          productKey = 'vip';
          break;
        default:
          break;
      }
      const productRes = await getProduct(productKey);
      if (productRes && productRes.coupons?.length > 0) {
        setGlobalDiscounts([...productRes.coupons.filter((item) => item.active && item.type === 'global')]);
        setOriginalProductCost(productRes.oneTimePrice);
        setProductCost(productRes.oneTimePrice);
      }
    };
    if (!chart?.PAYMENT_INFO?.alreadyPaid && chart?.PRODUCT_INFO?.selectedProduct) {
      init();
    }
  }, [chart?.PRODUCT_INFO?.selectedProduct, chart?.PAYMENT_INFO?.alreadyPaid]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        {/* <Header patientId={patientId} /> */}
        {chart?.PRODUCT_INFO?.selectedProduct ? (
          <div id="payment-page" className="flex w-screen flex-col items-center justify-center">
            <Heading id="payment-page-header" textContent="Payment Page" />
            <div id="payment-page-main" className="flex w-5/6 flex-col justify-around">
              <div className="flex flex-col">
                <div id="pricing-info">
                  <p className="text-xl font-bold" id="product-name">
                    Product: {chart.PRODUCT_INFO?.selectedProduct}
                  </p>
                  <p className="text-xl font-bold" id="product-initial-cost">
                    Cost: {alreadyPaid ? 'PAID' : originalProductCost === 999 ? 'Loading' : '$ ' + originalProductCost}
                  </p>
                  {discountAmt > 0 && (
                    <p className="text-xl font-bold" id="product-discount">
                      Discount: ${discountAmt}
                    </p>
                  )}
                  {discountAmt > 0 && (
                    <p className="text-xl font-bold" id="product-final-cost">
                      Final Cost: ${productCost}
                    </p>
                  )}
                </div>
                {originalProductCost === 999 ? (
                  <div className="ml-2">
                    <svg
                      aria-hidden="true"
                      className="inline h-10 w-10 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </div>
                ) : (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      codeForm.handleSubmit();
                    }}
                  >
                    <FormSelect
                      label="Are you arriving from one of our partner organizations entitling you to a discount?"
                      id="partner-org"
                      name="Partner"
                      value={partnerOrg}
                      required={true}
                      onBlur={(e) => setPartnerOrg(e.target.value)}
                      onChange={(e) => setPartnerOrg(e.target.value)}
                      options={[
                        { name: 'Please Select...', value: 'select' },
                        { name: 'No', value: 'no' },
                        { name: 'Yes', value: 'yes' },
                      ]}
                    />
                    {partnerOrg == 'yes' && (
                      <div id="org-coupon-info" className="my-4">
                        <p className="text-2xl font-bold">
                          Please enter <span className="italic">your organizational email </span>address below then
                          click the &quot;Request Discount&quot; button
                        </p>
                        <div className="flex flex-row">
                          <codeForm.Field
                            name="email"
                            children={(field) => (
                              <>
                                <FormInput
                                  label="Email"
                                  id="discount-email"
                                  name={field.name}
                                  value={field.state.value}
                                  handleBlur={field.handleBlur}
                                  handleChange={(e) => field.handleChange(e.target.value)}
                                  required={false}
                                />
                              </>
                            )}
                          />
                          <Button id="request-discount" type="submit" textContent="Request Discount" />
                          {requestingDiscount && <div>Requesting Discount...</div>}
                        </div>
                      </div>
                    )}

                    {partnerOrg == 'no' && (
                      <FormSelect
                        label="Do you have a referral code from an advisor or other source?"
                        id="referral-code"
                        name="Referral Code"
                        value={referralCode}
                        required={true}
                        onBlur={(e) => {
                          setReferralCode(e.target.value);
                          setHasDiscount(e.target.value === 'yes' ? true : false);
                        }}
                        onChange={(e) => {
                          setReferralCode(e.target.value);
                          setHasDiscount(e.target.value === 'yes' ? true : false);
                        }}
                        options={[
                          { name: 'Please Select...', value: 'select' },
                          { name: 'No', value: 'no' },
                          { name: 'Yes', value: 'yes' },
                        ]}
                      />
                    )}

                    {hasDiscount && (
                      <div id="code-input">
                        <p className="my-4 mt-6 text-2xl font-bold">
                          Please enter your code below and click the &quot;Apply Code&quot; button.
                        </p>
                        <div className="flex flex-row items-center">
                          <codeForm.Field
                            name="discountCode"
                            children={(field) => (
                              <FormInput
                                label="Referral Code"
                                id="discount-code"
                                name={field.name}
                                value={field.state.value}
                                handleBlur={field.handleBlur}
                                handleChange={(e) => field.handleChange(e.target.value)}
                                required={false}
                              />
                            )}
                          />
                          <Button
                            id="discount-apply"
                            type="button"
                            textContent={discountApplied ? 'Code Applied' : 'Apply Code'}
                            onClick={handleApplyDiscount}
                            disabled={discountApplied}
                          />
                          {checkingDiscount && (
                            <div>
                              <p className="ml-4 text-xl">Checking Discount...</p>
                            </div>
                          )}
                          {!alreadyPaid && globalDiscounts?.length === 0 && (
                            <div>
                              <p className="ml-4 text-xl">Updated Referral Codes list. Please refresh to continue.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </form>
                )}
              </div>
              {readyToPay && !clientSecret ? <div>Connecting to Payment Provider...</div> : null}
              {readyToPay && clientSecret ? (
                <>
                  <div id="stripe-checkout" className="mt-8">
                    <StripeCheckout
                      clientSecret={clientSecret}
                      patientId={patientId}
                      amountPaid={productCost}
                      couponCode={discountApplied ? codeForm.state.values.discountCode : null}
                      discountApplied={discountAmt}
                    />
                  </div>
                </>
              ) : (
                <div className="mt-10 flex flex-col items-center justify-center">
                  {showPayNowButton() && (
                    <>
                      <p className="text-xl">
                        To avoid being overcharged, you should only click &quot;Pay Now&quot; after your discount has
                        been applied.
                      </p>
                      <p className="text-xl">
                        If you do not have any discounts, you can click the &quot;Pay Now&quot; button when ready.
                      </p>
                      <div>
                        <Button
                          type="button"
                          id="ready-to-pay"
                          textContent="Pay Now"
                          disabled={readyToPay && !clientSecret}
                          onClick={() => setReadyToPay(true)}
                        />
                      </div>
                    </>
                  )}
                  {(alreadyPaid && nextPageURL) || (productCost == 0 && nextPageURL) ? (
                    <NavForward to={nextPageURL} disabled={false} />
                  ) : null}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <p>Unable to determine product type</p>
          </div>
        )}
      </>
    );
  }

  return <div>Loading...</div>;
}
